import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import dayjs from "dayjs";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";

import apiClient from "../../../../utils/apiClient";
import { ArmeniaAct } from "./ArmeniaActsHistoryDrawer";

export type ArmeniaSignedActFile = {
  id: number;
  act_id: number;
  original_name: string;
  mime_type?: string;
  file_size?: number;
  uploaded_by?: number;
  created_at?: string;
  updated_at?: string;
};

type Props = {
  act: ArmeniaAct | null;
  onPreview: (file: ArmeniaSignedActFile) => void | Promise<void>;
};

const formatSize = (value?: number) => {
  const size = Number(value ?? 0);

  if (!size) return "";

  const kb = size / 1024;

  if (kb < 1024) {
    return `${kb.toFixed(2)} KB`;
  }

  return `${(kb / 1024).toFixed(2)} MB`;
};

const ArmeniaActSignedFilesDrawer = ({ act, onPreview }: Props) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState<ArmeniaSignedActFile[]>([]);
  const [loading, setLoading] = useState(false);

  const loadFiles = async () => {
    if (!act?.id) return;

    try {
      setLoading(true);

      const response = await apiClient.get(`/acts/${act.id}/signed-files`);

      if (response.data.success) {
        setFiles(response.data.files ?? []);
      }
    } catch (error) {
      console.error("Ошибка загрузки файлов подписанного акта:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file: ArmeniaSignedActFile) => {
    try {
      const response = await apiClient.get(`/acts/signed-files/${file.id}`, {
        responseType: "blob",
      });

      FileSaver.saveAs(new Blob([response.data]), file.original_name);
    } catch (error) {
      console.error("Ошибка скачивания файла:", error);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [act?.id]);

  const columns = useMemo<GridColDef<ArmeniaSignedActFile>[]>(
    () => [
      {
        field: "original_name",
        headerName: t("armeniaAct.signedFiles.file"),
        flex: 2,
      },
      {
        field: "mime_type",
        headerName: t("armeniaAct.signedFiles.type"),
        flex: 1,
        valueGetter: (_, row) => row.mime_type || "",
      },
      {
        field: "file_size",
        headerName: t("armeniaAct.signedFiles.size"),
        width: 130,
        valueGetter: (_, row) => formatSize(row.file_size),
      },
      {
        field: "created_at",
        headerName: t("armeniaAct.signedFiles.uploadedAt"),
        width: 170,
        valueGetter: (_, row) =>
          row.created_at
            ? dayjs(row.created_at).format("DD.MM.YYYY HH:mm")
            : "",
      },
      {
        field: "actions",
        headerName: "",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onPreview(params.row);
              }}
            >
              {t("armeniaAct.signedFiles.preview")}
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(params.row);
              }}
            >
              {t("armeniaAct.signedFiles.download")}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {t("armeniaAct.signedFiles.title")}: <strong>{files.length}</strong>
        </Typography>

        {loading && <CircularProgress size={18} />}
      </Stack>

      <DataGrid
        rows={files}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        hideFooter
        loading={loading}
        sx={{
          "--DataGrid-rowBorderColor": "#D1D3D4",
          "& .MuiDataGrid-cell": {
            fontSize: 14,
            borderTop: "0.5px solid #D1D3D4",
            display: "flex",
            alignItems: "center",
          },
          fontFamily: "inherit",
          borderWidth: 0,
          background: "#ffffff",
        }}
      />
    </Box>
  );
};

export default ArmeniaActSignedFilesDrawer;
